import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { apiInstance } from '../axiosApi';

export const manualCall = createAsyncThunk('call/manual_call', async (payload) => {
  return await apiInstance.post('/call_originate/manual_call', payload);
});

// Call-path fetches carry an explicit timeout: apiInstance sets none, so without it
// a stalled request never rejects — the caller's retry/watchdog machinery never gets
// control back, and the agent is left off the CRM for the whole call.
const CALL_FETCH_TIMEOUT_MS = 15000;

export const crmInformation = createAsyncThunk('call/crm_info', async (payload) => {
  return await apiInstance.post('/crms_info', payload, { timeout: CALL_FETCH_TIMEOUT_MS });
});

export const manualCrmInfo = createAsyncThunk('call/manual_crm_info', async () => {
  return await apiInstance.get('/manual-crm-info/', { timeout: CALL_FETCH_TIMEOUT_MS });
});

export const subDispositions = createAsyncThunk('call/sub_disposition', async (payload) => {
  return await apiInstance.post('/disposition/sub_disposition', payload);
});

export const hangupCall = createAsyncThunk('call/hangup_call', async (payload) => {
  return await apiInstance.post('/call_originate/hangup_call', payload);
});

export const subSubDispositions = createAsyncThunk('call/sub_sub_disposition', async (payload) => {
  return await apiInstance.post('/disposition/sub_sub_disposition', payload);
});

export const updateCrmInformation = createAsyncThunk('call/crms_info_update', async (payload) => {
  return await apiInstance.post('/crms_info/update', payload);
});

export const followUpAdd = createAsyncThunk('call/follow-up', async (payload) => {
  return await apiInstance.post('/follow-up/', payload);
});

export const breaksListGet = createAsyncThunk('call/breaks', async (params) => {
  return await apiInstance.get('/breaks', { params });
});

export const followUpListGet = createAsyncThunk('call/follow-up/list', async (params) => {
  return await apiInstance.get('/follow-up/list', { params });
});

export const selectBreak = createAsyncThunk('call/breaks/agent_break', async (payload) => {
  return await apiInstance.post('/breaks/agent_break', payload);
});

export const callMuteUnmute = createAsyncThunk(
  'call/call_originate/mute_unmute',
  async (payload) => {
    return await apiInstance.post('/call_originate/mute_unmute', payload);
  }
);

export const callHoldUnhold = createAsyncThunk(
  'call/call_originate/hold_unhold',
  async (payload) => {
    return await apiInstance.post('/call_originate/hold_unhold', payload);
  }
);

export const manualDialing = createAsyncThunk(
  'call/call_originate/manual_dialing',
  async (payload) => {
    return await apiInstance.post('/call_originate/manual_dialing', payload);
  }
);

export const progressiveCall = createAsyncThunk(
  'call/call_originate/progressive_call',
  async () => {
    return await apiInstance.post('/call_originate/progressive_call');
  }
);

export const previewCallList = createAsyncThunk(
  'call/call_originate/preview_call_list',
  async () => {
    return await apiInstance.get('/call_originate/preview_call_list');
  }
);

export const previewCall = createAsyncThunk('call/call_originate/preview_call', async (payload) => {
  return await apiInstance.post('/call_originate/preview_call', payload);
});

export const crmInfoLog = createAsyncThunk('call/auth/log', async (payload) => {
  return await apiInstance.post('/auth/log', payload);
});

export const setIdleCall = createAsyncThunk('call/call_originate/idle_call', async () => {
  return await apiInstance.post('/call_originate/idle_call', {});
});

export const setUnregisterCall = createAsyncThunk(
  'call/call_originate/unregister_call',
  async () => {
    return await apiInstance.post('/call_originate/unregister_call', {});
  }
);

export const dtmfPress = createAsyncThunk('call/call_originate/dtmf', async (payload) => {
  return await apiInstance.post('/call_originate/dtmf', payload);
});

export const blindTransfer = createAsyncThunk(
  'call/call_originate/blind_transfer',
  async (payload) => {
    return await apiInstance.post('/call_originate/blind_transfer', payload);
  }
);

export const addConference = createAsyncThunk(
  'call/conference/originate_conference',
  async (payload) => {
    return await apiInstance.post('/conference/originate_conference', payload);
  }
);

export const handleConference = createAsyncThunk(
  'call/conference/handle_conference_status',
  async (payload) => {
    return await apiInstance.post('/conference/handle_conference_status', payload);
  }
);

export const attendedTransfer = createAsyncThunk(
  'call/call_originate/attended_transfer',
  async (payload) => {
    return await apiInstance.post('/call_originate/attended_transfer', payload);
  }
);

export const userOptionGetByProcessId = createAsyncThunk('call_originate/user', async (params) => {
  return await apiInstance.get('/user', { params });
});

export const transferUserOptionGet = createAsyncThunk('call_originate/user/transfer', async () => {
  return await apiInstance.get('/user/transfer');
});

// Transfer targets for Process / Queue (blind + attended transfer).
export const transferProcessOptionGet = createAsyncThunk(
  'call_originate/transfer/process',
  async () => {
    return await apiInstance.get('/process');
  }
);

export const transferQueueOptionGet = createAsyncThunk(
  'call_originate/transfer/queues',
  async () => {
    return await apiInstance.get('/queues');
  }
);

export const transferIvrOptionGet = createAsyncThunk(
  'call_originate/transfer/ivrs',
  async () => {
    return await apiInstance.get('/ivrs');
  }
);

// User-to-user (agent-to-agent) internal call.
export const userCall = createAsyncThunk('call/call_originate/user_call', async (payload) => {
  return await apiInstance.post('/call_originate/user_call', payload);
});

export const hotKeyOptionGet = createAsyncThunk('call_originate/hot-key', async (process_id) => {
  return await apiInstance.get(`/hot-key/process/${process_id}`);
});

export const userScriptGet = createAsyncThunk('call_originate/scripts', async (id) => {
  return await apiInstance.get(`/scripts/${id}`);
});

export const followUpStatusUpdate = createAsyncThunk(
  'follow-up/follow-up/edit-status',
  async (payload) => {
    const { id, ...rest } = payload;
    return await apiInstance.put(`/follow-up/edit-status/${id}`, rest);
  }
);

export const pauseModeUpdate = createAsyncThunk('user_login/auto-call-status', async (payload) => {
  return await apiInstance.put('/user_login/auto-call-status', payload);
});

const initialState = {
  crmDetails: null,
  breaksList: [],
  pauseMode: 1
};

const callingSlice = createSlice({
  name: 'calling',
  initialState,
  reducers: {
    clearCallingSlice: () => initialState,
    setPauseMode(state, action) {
      state.pauseMode = action.payload;
    },
    removeCrmDetails(state, action) {
      state.crmDetails = null;
    }
  },
  extraReducers: (builder) => {
    builder.addCase(crmInformation.fulfilled, (state, action) => {
      state.crmDetails = action?.payload?.data;
    });
    builder.addCase(breaksListGet.fulfilled, (state, action) => {
      state.breaksList = action?.payload?.data;
    });
  }
});

export default callingSlice.reducer;
export const { clearCallingSlice, removeCrmDetails, setPauseMode } = callingSlice.actions;

export const selectCrmDetails = (state) => state.calling.crmDetails;
export const useCrmDetails = () => {
  const crmDetails = useSelector(selectCrmDetails);
  return useMemo(() => crmDetails, [crmDetails]);
};

export const selectPauseMode = (state) => state.calling.pauseMode;
export const usePauseMode = () => {
  const pauseMode = useSelector(selectPauseMode);
  return useMemo(() => pauseMode, [pauseMode]);
};

export const selectBreaksList = (state) => state.calling.breaksList;
export const useBreaksList = () => {
  const breaksList = useSelector(selectBreaksList);
  return useMemo(() => breaksList, [breaksList]);
};
